import type { AutopayDetailsResponse } from "@credit-acceptance/step6-bff-client";
import { PlusIcon } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useState } from "react";
import { type Control, type UseFieldArrayAppend, type UseFieldArrayRemove, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import type { SectionRegistrar } from "../../../../../hooks/useScrollSpy";
import type { ScrollSpySection } from "../../../../stipulations/stipulationCategoryFilters";
import type { SignerChecklist } from "../../../checklist/submitChecklist";
import { useRetryWorknumberRecalc } from "../api/useRetryWorknumberRecalc";
import { type ApplicantSsnType, MAX_INCOMES_PER_PERSON } from "../constants";
import type { IncomeInput, SignersInput } from "../Signers.schema";
import { makeEmptyIncome } from "../Signers.schema";
import { AutoPayInformation } from "./AutoPayInformation";
import { BankInformation } from "./BankInformation";
import { CustomerInformation } from "./CustomerInformation";
import { IncomeBlock } from "./IncomeBlock";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

export interface SignerColumnProperties {
  title: string;
  personPath: PersonPath;
  control: Control<SignersInput>;
  fields: Array<IncomeInput & { id: string }>;
  append: UseFieldArrayAppend<SignersInput, `${PersonPath}.incomes`>;
  remove: UseFieldArrayRemove;
  /** Deal / approval id, threaded down to each income block's employer-info flow. */
  approvalId?: string;
  /** Real applicant id for this person, threaded down to the Work Number flow. */
  applicantId?: string;
  /** Applicant SSN/ITIN status — filters the Income Type list and seeds new blocks. */
  applicantSsnType: ApplicantSsnType;
  /** Registers this column's Income/Bank/Residence sections with the Signers-level scroll-spy. */
  registerSection: SectionRegistrar<ScrollSpySection>;
  /** Save-error message for this signer, if the last save attempt failed. */
  errorMessage?: string;
  /** Forwarded to CustomerInformation's `identityReadOnly` — see that prop's doc comment. */
  identityReadOnly: boolean;
  /** Deal-level AutoPay details from the dashboard response; undefined when the read failed. */
  autopay?: AutopayDetailsResponse;
  /** Submit checklist verdicts for this signer's subsections. */
  checklist: SignerChecklist;
}

/**
 * One signer's content, split into independent pieces instead of one rendered
 * tree. Signers.tsx places matching pieces from both signers (Driver / 2nd
 * Signer) in the same CSS grid row, so e.g. both "Bank Information" sections
 * start at the same Y regardless of how tall either signer's content above it
 * happens to be (differing income-block counts, open/collapsed blocks, an
 * extra warning banner, differently-wrapped text, etc.) — the shorter side
 * just leaves blank space in that row instead of drifting out of alignment.
 */
export interface SignerColumnSections {
  header: ReactNode;
  /** Combined error / recalc-deferred / employer-info-not-found banners, or null when none apply. */
  banners: ReactNode;
  /**
   * One entry per income block, in field-array order. Typed as elements
   * (not bare ReactNode) so a caller mapping over this array — Signers.tsx's
   * shared grid — can read each entry's own `.key` (the field's stable id)
   * instead of keying its own wrapper by array index, which would let React
   * misattribute a block's local state (open/collapsed, calculator) to the
   * wrong income after a row in the middle is removed.
   */
  incomeBlocks: ReactElement[];
  totalRow: ReactNode;
  bankInformation: ReactNode;
  autoPayInformation: ReactNode;
  customerInformation: ReactNode;
}

// USD, always two decimals (e.g. $4,200.00). Built once at module scope.
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats the combined gross income for display, defaulting an absent value to $0.00.
 * @param {number | undefined} amount - The summed monthly gross for the signer.
 * @returns {string} A formatted USD string, e.g. "$4,200.00".
 */
function formatTotalGrossIncome(amount: number | undefined): string {
  return USD_FORMATTER.format(amount ?? 0);
}

// ---------------------------------------------------------------------------
// useSignerColumnSections
// ---------------------------------------------------------------------------
export function useSignerColumnSections({
  title,
  personPath,
  control,
  fields,
  append,
  remove,
  approvalId,
  applicantId,
  applicantSsnType,
  registerSection,
  errorMessage,
  identityReadOnly,
  autopay,
  checklist,
}: SignerColumnProperties): SignerColumnSections {
  const { t } = useTranslation();
  const atCap = fields.length >= MAX_INCOMES_PER_PERSON;

  // Total Combined Gross Income — live sum of every income block's Gross Monthly
  // Pay for this signer; recomputes as blocks are added/removed/edited (and as
  // the calculator / Pay per Period updates each block's Gross Monthly Pay).
  const incomes = useWatch({ control, name: `${personPath}.incomes` }) as IncomeInput[] | undefined;
  const totalGrossMonthlyPay = (incomes ?? []).reduce(
    (sum, income) => sum + (Number(income?.monthlyGrossPayAmount) || 0),
    0
  );

  function handleAddIncome() {
    if (!atCap) {
      append(makeEmptyIncome(applicantSsnType));
    }
  }

  // Employer-stips recalc-deferred banner (OR-23544) — one per applicant, not
  // per income block, since the recalc itself is approval-scoped. Any income
  // block's pull can set this; the retry button clears it optimistically and
  // re-sets it if the retry itself fails or comes back still deferred.
  const [recalcDeferred, setRecalcDeferred] = useState(false);
  const retryRecalc = useRetryWorknumberRecalc();

  // Purchased Employer Info "not found" warning — one per applicant, driven by
  // the applicant's W2 income block's eligibility check (`lastPullStatus === "NOTFOUND"`).
  const [employerInfoNotFound, setEmployerInfoNotFound] = useState(false);

  function handleUpdateEmployerStips() {
    setRecalcDeferred(false);
    retryRecalc.mutate(
      { approvalId: approvalId ?? "", applicantId: applicantId ?? "" },
      { onError: () => setRecalcDeferred(true) }
    );
  }

  function handleWorknumberRecalcDeferred(deferred: boolean) {
    setRecalcDeferred(deferred);
  }

  const header = (
    <div className="border-border border-b pb-2">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
    </div>
  );

  const hasBanners = Boolean(errorMessage) || recalcDeferred || employerInfoNotFound;
  const banners = hasBanners ? (
    <div className="flex flex-col gap-1.5">
      {errorMessage && (
        <p role="alert" className="text-destructive text-xs">
          {errorMessage}
        </p>
      )}

      {recalcDeferred && (
        <div
          role="alert"
          className="bg-warning text-warning-foreground flex items-center justify-between gap-3 rounded-sm px-3 py-2 text-xs"
        >
          <span>{t("review.income.employerHistory.recalcDeferred")}</span>
          <Button
            type="button"
            variant="link"
            size="xs"
            className="text-warning-foreground h-auto shrink-0 cursor-pointer p-0 underline"
            disabled={retryRecalc.isPending}
            onClick={handleUpdateEmployerStips}
          >
            {t("review.income.employerHistory.updateEmployerStips")}
          </Button>
        </div>
      )}

      {employerInfoNotFound && (
        <p role="alert" className="text-destructive text-xs">
          {t("review.income.employerHistory.notFoundLastPull")}
        </p>
      )}
    </div>
  ) : null;

  // Every income block registers under the same "Income" section value for
  // this column — registerSection's registrar supports multiple elements
  // sharing one (category, applicant) pair, so the section stays "active" as
  // long as ANY block is in the activation band, not just whichever block
  // happened to be first.
  const incomeBlocks = fields.map((field, index) => (
    <div key={field.id} ref={registerSection("Income", personPath)}>
      <IncomeBlock
        control={control}
        personPath={personPath}
        index={index}
        hasChecklistWarning={checklist.income}
        onRemove={index > 0 ? () => remove(index) : undefined}
        approvalId={approvalId}
        applicantId={applicantId}
        applicantSsnType={applicantSsnType}
        onWorknumberRecalcDeferred={handleWorknumberRecalcDeferred}
        onEmployerInfoLastPullNotFound={setEmployerInfoNotFound}
        employerInfoNotFound={employerInfoNotFound}
      />
    </div>
  ));

  const totalRow = (
    <div className="flex items-center justify-between gap-3">
      <p className="flex items-baseline gap-2">
        <span className="text-foreground text-sm font-semibold">{formatTotalGrossIncome(totalGrossMonthlyPay)}</span>
        <span className="text-muted-foreground text-xs">{t("signers.totalCombinedGrossIncome")}</span>
      </p>
      {/* Add 2nd / 3rd Income — hidden at cap */}
      {!atCap && (
        <Button
          type="button"
          variant="link"
          size="sm"
          onClick={handleAddIncome}
          className="h-auto p-0 text-xs underline"
        >
          <PlusIcon className="size-3" aria-hidden="true" />
          {t("signers.addIncome", { position: fields.length === 1 ? "2nd" : "3rd" })}
        </Button>
      )}
    </div>
  );

  const bankInformation = (
    <BankInformation
      control={control}
      personPath={personPath}
      hasChecklistWarning={checklist.bank}
      sectionRef={registerSection("Bank", personPath)}
    />
  );

  const autoPayInformation = <AutoPayInformation personPath={personPath} autopay={autopay} approvalId={approvalId} />;

  const customerInformation = (
    <CustomerInformation
      control={control}
      personPath={personPath}
      hasChecklistWarning={checklist.residence}
      applicantSsnType={applicantSsnType}
      identityReadOnly={identityReadOnly}
      residenceSectionRef={registerSection("Residence", personPath)}
      approvalId={approvalId}
      applicantId={applicantId}
    />
  );

  return { header, banners, incomeBlocks, totalRow, bankInformation, autoPayInformation, customerInformation };
}
